// A small durable outbox for the external report deliveries.
//
// Jobs are persisted (localStorage), so a report that could not be sent before
// the tab closed, or while the reports server was down, is retried on the next
// visit instead of being lost. Delivery is:
//   - strictly one job at a time;
//   - newest-wins per group: a newer job for the same group supersedes older
//     pending ones (reports are cumulative score updates, so only the latest
//     state matters, and a backlog collapses into one call);
//   - retried with exponential backoff + jitter, up to a maximum number of
//     attempts, after which the job is kept as "dead" (visible in stats(), never
//     retried) rather than looping forever;
//   - guarded by a circuit breaker: after several consecutive failures no
//     attempts are made for a cool-down period, so an outage is not hammered.
//
// Everything time- and storage-related is injectable so it can be unit tested.

const DEFAULTS = {
  key: "diwaliReportOutbox",
  maxAttempts: 6,
  baseDelayMs: 2000,
  maxDelayMs: 60000,
  breakerThreshold: 3,
  breakerCooldownMs: 30000,
};

export function createOutbox({
  deliver,
  canDeliver = () => true,
  storage = null,
  now = () => Date.now(),
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (id) => clearTimeout(id),
  random = Math.random,
  ...options
} = {}) {
  const cfg = { ...DEFAULTS, ...options };
  let seq = 0;
  let jobs = load();
  let draining = null;
  let timer = null;
  let consecutiveFailures = 0;
  let breakerUntil = 0;

  function load() {
    try {
      const parsed = JSON.parse((storage && storage.getItem(cfg.key)) || "[]");
      if (!Array.isArray(parsed)) return [];
      parsed.forEach((j) => {
        seq = Math.max(seq, j.seq || 0);
      });
      return parsed;
    } catch {
      return [];
    }
  }

  function save() {
    try {
      if (storage) storage.setItem(cfg.key, JSON.stringify(jobs));
    } catch {
      /* storage full or unavailable: the in-memory queue still works */
    }
  }

  function backoff(attempts) {
    const exp = Math.min(cfg.maxDelayMs, cfg.baseDelayMs * 2 ** (attempts - 1));
    return Math.round(exp / 2 + random() * (exp / 2)); // 50-100% of the step
  }

  // The job to try next: the newest live job of the first group that has one.
  function nextJob() {
    const live = jobs.filter((j) => !j.dead && canDeliver(j.payload));
    if (live.length === 0) return null;
    const group = live[0].group;
    const newest = live.filter((j) => j.group === group).reduce((a, b) => (b.seq > a.seq ? b : a));
    return newest;
  }

  function armTimer() {
    if (timer !== null) {
      cancel(timer);
      timer = null;
    }
    const live = jobs.filter((j) => !j.dead && canDeliver(j.payload));
    if (live.length === 0) return;
    const at = Math.max(breakerUntil, Math.min(...live.map((j) => j.nextAt || 0)));
    timer = schedule(() => {
      timer = null;
      drain();
    }, Math.max(0, at - now()));
  }

  async function run() {
    for (;;) {
      const job = nextJob();
      if (!job) break;
      const t = now();
      if (t < breakerUntil || (job.nextAt || 0) > t) break; // not due yet: the timer wakes us
      // Anything older for the same group is superseded by this (newer) job.
      jobs = jobs.filter((j) => j.group !== job.group || j.seq >= job.seq || j.dead);
      save();
      try {
        await deliver(job.payload);
        jobs = jobs.filter((j) => j.id !== job.id);
        consecutiveFailures = 0;
      } catch (err) {
        const live = jobs.find((j) => j.id === job.id);
        if (live) {
          live.attempts = (live.attempts || 0) + 1;
          if (live.attempts >= cfg.maxAttempts) {
            live.dead = true;
            live.error = String((err && err.message) || err);
          } else {
            live.nextAt = now() + backoff(live.attempts);
          }
        }
        consecutiveFailures += 1;
        if (consecutiveFailures >= cfg.breakerThreshold) {
          breakerUntil = now() + cfg.breakerCooldownMs;
          consecutiveFailures = 0;
        }
      }
      save();
    }
    armTimer();
  }

  function drain() {
    if (!draining) {
      draining = run().finally(() => {
        draining = null;
      });
    }
    return draining;
  }

  return {
    // Adds (or replaces, by id) a job and starts delivering.
    enqueue({ id, group = "default", payload }) {
      jobs = jobs.filter((j) => j.id !== id);
      jobs.push({ id, group, payload, seq: ++seq, attempts: 0, nextAt: 0, dead: false });
      save();
      return drain();
    },
    drain,
    // Resolves when the current delivery pass has finished.
    whenIdle: () => draining || Promise.resolve(),
    stats: () => ({
      pending: jobs.filter((j) => !j.dead).length,
      dead: jobs.filter((j) => j.dead).length,
      breakerOpen: now() < breakerUntil,
      consecutiveFailures,
    }),
    // Drops everything and stops the retry timer (tests, or when a player signs out).
    reset() {
      if (timer !== null) cancel(timer);
      timer = null;
      jobs = [];
      consecutiveFailures = 0;
      breakerUntil = 0;
      save();
    },
    // For diagnostics / tests.
    jobs: () => jobs.map((j) => ({ ...j })),
  };
}
